import { useCallback } from 'react';
import { useQueryClient, useQueryDetails } from '@gogame/query';
import { useScreenState } from '@gogame/screens';

import { SCOPE_PAGES } from 'scenes/Gallery/ConversionPages/constants';
import { IDS } from 'scenes/Gallery/ConversionPages/ConversionGroups/constants';
import {
  loadConversionGroupDetails,
  markAsReadConcepts,
} from 'scenes/Gallery/ConversionPages/ConversionGroups/sources/conversion-groups';

import {
  conversionGroupsDetails,
  conversionGroupsGrids,
  conversionGroupsGrid,
} from './queryKeyFactory';

const useCurrentGridParams = ({ isOptimisticResetIsNew }) => {
  const screenState = useScreenState();

  if (!screenState || !isOptimisticResetIsNew) {
    return null;
  }
  const {
    [SCOPE_PAGES]: { tableSettings },
  } = screenState;

  return {
    tableSettings: tableSettings,
    filters: screenState.filters,
    searchQuery: screenState.searchQuery,
  };
};

export const useLoadConversionGroupDetails = ({
  id,
  isOptimisticResetIsNew = false,
}) => {
  const queryClient = useQueryClient();

  const gridParams = useCurrentGridParams({ isOptimisticResetIsNew });

  //optimistic update
  const optimisticResetIsNew = useCallback(async () => {
    if (gridParams) {
      queryClient.setQueryData(
        conversionGroupsGrid(gridParams),
        (oldData = {}) => {
          return {
            ...oldData,
            rows: oldData?.rows?.map((row) => {
              if (row?.id === id) {
                row.isNew = false;
              }
              return row;
            }),
          };
        }
      );
    }
  }, [queryClient, gridParams]);

  return useQueryDetails(
    conversionGroupsDetails(id),
    async (_, { signal }) => {
      await Promise.all([
        markAsReadConcepts({ [IDS]: [id] }),
        optimisticResetIsNew(),
      ]);
      return loadConversionGroupDetails({ id }, { signal });
    },
    {
      initialData: () => {
        return queryClient
          .getQueryData(conversionGroupsGrids(), { exact: false, active: true })
          ?.rows?.find((d) => d.id === id);
      },
    }
  );
};
